import { useEffect, useRef } from 'react';
import { track, TRACK } from '../../libs/teads-tracking';
import { SHOW_DEBUG } from '../../constants';
import './lens-track-zones.css';

// The scene switches, ingredient toggles and the restart control are buttons
// the LENS draws on the canvas; Camera Kit gives the page no event when they
// are tapped (the only lens->page channel, RemoteApiService, requires the
// lens itself to be built against a registered API spec - this one is not).
// These zones mirror the buttons' screen positions and log the tap WITHOUT
// consuming it: the listener neither prevents nor stops the pointer event
// and the debug circles are pointer-events: none, so the lens keeps
// receiving the touch.
//
// Positions are fractions of the viewport, measured off a real iPhone
// (390x844 class) screenshot of the live lens; r is design pt incl. tap
// slop. To verify on a device, open any page with ?zones appended (e.g.
// /de?zones) - the zones render as dashed circles; SHOW_DEBUG shows them
// too.
const ZONES = [
    { id: 'stove', action: TRACK.AR_STOVE, x: 0.113, y: 0.643, r: 28 },
    { id: 'table', action: TRACK.AR_TABLE, x: 0.113, y: 0.724, r: 28 },
    { id: 'restart', action: TRACK.AR_RESTART, x: 0.127, y: 0.939, r: 28 },
    { id: 'ingredient-1', action: TRACK.INGREDIENTS, x: 0.865, y: 0.548, r: 28 },
    { id: 'ingredient-2', action: TRACK.INGREDIENTS, x: 0.880, y: 0.638, r: 34 },
    { id: 'ingredient-3', action: TRACK.INGREDIENTS, x: 0.865, y: 0.730, r: 28 },
];

const SHOW_ZONES = SHOW_DEBUG
    || (typeof window !== 'undefined' && /[?&]zones\b/.test(window.location.search));

// proof-of-life for debugging sessions: says whether the ?zones flag parsed
console.log('[lens-track-zones] visible=' + SHOW_ZONES
    + ' search="' + (typeof window !== 'undefined' ? window.location.search : '') + '"');

const LensTrackZones = ({ active }) => {
    const activeRef = useRef(active);
    activeRef.current = active;

    useEffect(() => {
        function onPointerDown(event) {
            if (!activeRef.current) return;
            // same freeze-at-480px rule as the --px unit in app-ui.css
            const scale = Math.min(window.innerWidth, 480) / 393;
            for (const zone of ZONES) {
                const dx = event.clientX - zone.x * window.innerWidth;
                const dy = event.clientY - zone.y * window.innerHeight;
                if (Math.hypot(dx, dy) <= zone.r * scale) {
                    track(zone.action);
                    return;
                }
            }
        }
        window.addEventListener('pointerdown', onPointerDown, { passive: true });
        return () => window.removeEventListener('pointerdown', onPointerDown);
    }, []);

    if (!SHOW_ZONES) return null;
    return (
        <div className="lens-track-zones" aria-hidden="true">
            <span className="lens-track-zones-badge">ZONES DEBUG</span>
            {ZONES.map(zone => (
                <div key={zone.id}
                     className="lens-track-zone"
                     style={{
                         left: `${zone.x * 100}%`,
                         top: `${zone.y * 100}%`,
                         width: `calc(${zone.r * 2} * var(--px))`,
                         height: `calc(${zone.r * 2} * var(--px))`,
                     }}>
                    <span className="lens-track-zone-label">{zone.id}</span>
                </div>
            ))}
        </div>
    );
};

export default LensTrackZones;
